/**
 * Rédiger une démonstration : de la prose **contenant** des mathématiques.
 *
 * Mesuré le 25 août 2026, et c'est le trou que ce composant bouche. Les deux
 * prototypes avaient découpé le problème en deux moitiés dont aucune ne sert
 * Mehdi : MathLive fait des formules sans prose, la page de rédaction faisait de
 * la prose sans clavier mathématique. Ce découpage était justifié en toutes
 * lettres — « sur les 502 exercices du pool de terminale, 502 passent par la voie
 * grille […] aucun ne demande le clavier mathématique ». La prémisse est fausse :
 * une démonstration par récurrence est de la prose *contenant* de la notation.
 *
 * Ce que la séance a produit, faute de mieux : `Un=3ⁿ+1`, avec un exposant Unicode
 * arraché au sélecteur de caractères d'un téléphone, là où il fallait `U_n` et
 * `3^n`. Douze minutes quarante-sept, 668 frappes, 105 caractères retenus.
 *
 * **Le clavier système reste le clavier de la prose**, et c'est délibéré : il est
 * rapide, il connaît la correction orthographique, et Mehdi écrit surtout du
 * texte. MathLive n'apparaît que pour la formule, à la demande, et rend la main
 * aussitôt.
 *
 * **Le délimiteur est celui du projet, `\(…\)`** — celui que `TexteMathematique`
 * lit déjà pour rendre les énoncés. Rien de nouveau n'est inventé ici : ce qu'un
 * élève écrit et ce qu'un énoncé porte se lisent de la même façon, donc l'aperçu
 * est rendu par le même code que le reste de l'application.
 */

import { Suspense, lazy, useCallback, useId, useRef, useState } from "react";

import { TexteMathematique } from "@/ui/TexteMathematique";
import type { MathAnswer } from "@/math/mathjson";

const MathAnswerField = lazy(() =>
  import("@/math/MathAnswerField").then((module) => ({ default: module.MathAnswerField })),
);

/** Ce que le champ mathématique reçoit quand il sert d'insertion. */
const SPEC_LIBRE = { kind: "symbolic", equivalence: "symbolic" } as const;

type Props = {
  value: string;
  onChange: (texte: string) => void;
  disabled?: boolean;
  label: string;
  placeholder?: string;
  rows?: number;
  /**
   * Y a-t-il des formules à insérer dans cette prose-là ?
   *
   * **Non en français, en anglais, en histoire ou en philosophie**, et c'est la
   * majorité des exercices à grille. Ce composant a été écrit pour les
   * démonstrations de Mehdi et servi partout ; Hedi l'a vu proposer « insérer
   * une formule ∑ » sur un exercice de français de Baya.
   *
   * Le bouton disparaît alors, et avec lui les 800 ko de MathLive qu'il aurait
   * chargés — la zone de texte et son clavier système suffisent. L'aperçu, lui,
   * reste conditionné à la présence réelle d'une formule dans le texte : s'il en
   * arrivait une par un autre chemin, elle se rendrait quand même.
   */
  formules?: boolean;
};

/** Insérer `fragment` dans `texte` à la position donnée, en gérant les espaces. */
export function inserer(texte: string, position: number, latex: string): {
  texte: string;
  curseur: number;
} {
  const nettoye = latex.trim();
  if (!nettoye) return { texte, curseur: position };
  const avant = texte.slice(0, position);
  const apres = texte.slice(position);
  // **Une espace de chaque côté, sauf si elle y est déjà.** Sans ça, « vaut\(3\) »
  // se rend collé au mot précédent, et l'élève croit avoir mal écrit alors que
  // c'est l'insertion qui a mangé l'espace.
  const separateurAvant = avant && !/\s$/.test(avant) ? " " : "";
  const separateurApres = apres && !/^\s/.test(apres) ? " " : "";
  const fragment = `${separateurAvant}\\(${nettoye}\\)${separateurApres}`;
  return { texte: avant + fragment + apres, curseur: avant.length + fragment.length };
}

export function RedactionField({
  value,
  onChange,
  disabled,
  label,
  placeholder,
  rows = 8,
  formules = true,
}: Props) {
  const zoneRef = useRef<HTMLTextAreaElement | null>(null);
  const positionRef = useRef(0);
  const [compose, setCompose] = useState(false);
  const [formule, setFormule] = useState<MathAnswer>({ latex: "", mathjson: null });
  const apercuId = useId();

  const memoriserPosition = useCallback(() => {
    const zone = zoneRef.current;
    if (zone) positionRef.current = zone.selectionStart ?? zone.value.length;
  }, []);

  const ouvrir = () => {
    memoriserPosition();
    setFormule({ latex: "", mathjson: null });
    setCompose(true);
  };

  const valider = () => {
    const { texte, curseur } = inserer(value, positionRef.current, formule.latex);
    onChange(texte);
    setCompose(false);
    // Rendre la main au texte, curseur après la formule : sans ça il faut viser
    // la zone au doigt pour continuer la phrase qu'on était en train d'écrire.
    requestAnimationFrame(() => {
      const zone = zoneRef.current;
      if (!zone) return;
      zone.focus();
      zone.setSelectionRange(curseur, curseur);
      positionRef.current = curseur;
    });
  };

  return (
    <div className="redaction">
      <label className="field">
        <span className="field__label">{label}</span>
        <textarea
          ref={zoneRef}
          className="textarea"
          rows={rows}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onSelect={memoriserPosition}
          onKeyUp={memoriserPosition}
          onClick={memoriserPosition}
          placeholder={placeholder}
          disabled={disabled}
        />
      </label>

      {!formules ? null : compose ? (
        <div className="redaction__compose" role="group" aria-label="Composer une formule">
          <Suspense fallback={<p className="muted">Je prépare le clavier…</p>}>
            <MathAnswerField
              spec={SPEC_LIBRE}
              value={formule.latex}
              onChange={setFormule}
              label="La formule à insérer"
            />
          </Suspense>
          <div className="redaction__actions">
            <button type="button" className="button" onClick={valider} disabled={!formule.latex.trim()}>
              insérer dans le texte
            </button>
            <button type="button" className="button button--ghost" onClick={() => setCompose(false)}>
              annuler
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="button button--ghost" onClick={ouvrir} disabled={disabled}>
          insérer une formule <span aria-hidden="true">∑</span>
        </button>
      )}

      {/* L'aperçu n'est pas une coquetterie : sans lui, `\(U_{n+1}\)` reste une
          suite de symboles, et l'élève ne sait pas s'il a écrit ce qu'il voulait
          avant de rendre. Rendu par le composant qui rend déjà les énoncés. */}
      {value.includes("\\(") ? (
        <div className="redaction__apercu" aria-describedby={apercuId}>
          <p id={apercuId} className="muted">Ce que ça donnera :</p>
          <TexteMathematique>{value}</TexteMathematique>
        </div>
      ) : null}
    </div>
  );
}
